import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";

import DistributionPie, {
  Distribution,
} from "../../ui/charts/distributionPie.component";
import { formatNumber, formatQuantity } from "../../../utils";
import {
  ComplaintsDistribution,
  PageUsage,
  ServiceDistributions,
  ValueQuantityMetric,
} from "../../../types/analytics";
import { useChartPalette } from "../../ui/charts/chartPalette";

interface ServiceDistributionChartsProps {
  stats: ServiceDistributions & Partial<PageUsage> & {
    complaints_distribution?: ComplaintsDistribution;
  };
  loading?: boolean;
  /**
   * Whether this equipment (or group) can print in colour. A mono-only machine
   * has no colour slice to show, and an always-zero one would still take a
   * legend row.
   */
  hasColorEquipment?: boolean;
}

/**
 * The "what was this period made of" pies shared by every statistics screen.
 *
 * Which pies appear follows the data the caller was given rather than a flag
 * per screen: the complaint split is only computed for the general and
 * technician views, page usage only where counters are in scope. Each pie's
 * full ranking is in the breakdown table further down the same page.
 */
export default function ServiceDistributionCharts({
  stats,
  loading = false,
  hasColorEquipment = true,
}: ServiceDistributionChartsProps) {
  const { t } = useTranslation();
  const palette = useChartPalette();

  const money = (value: number) => `${formatNumber(value)} zł`;
  const count = (value: number) => formatQuantity(value);

  // Which parts lead depends on what you measure - one drum against a box of
  // staples - so the two rankings are separate and the reader picks one.
  const [partsMetric, setPartsMetric] = useState<ValueQuantityMetric>("by_value");
  const partsByValue = partsMetric === "by_value";

  // Same for services: a rare overhaul can outweigh a hundred cleanings. How
  // often they were done stays the default - it is what this pie always showed.
  const [servicesMetric, setServicesMetric] =
    useState<ValueQuantityMetric>("by_quantity");
  const servicesByValue = servicesMetric === "by_value";

  const complaints = stats.complaints_distribution;

  /** Two slices, and they mean good and bad - so they wear the reserved status
   * colours rather than a categorical pair, and the legend names both. */
  const complaintsDistribution: Distribution | null = useMemo(() => {
    if (!complaints || complaints.total === 0) return null;
    return {
      items: [
        {
          label: t("Without complaints"),
          value: complaints.without_complaints,
          is_other: false,
        },
        {
          label: t("With complaints"),
          value: complaints.with_complaints,
          is_other: false,
        },
      ].filter((item) => item.value > 0),
      total: complaints.total,
      other_count: 0,
    };
  }, [complaints, t]);

  const complaintRate = complaints?.total
    ? (complaints.with_complaints / complaints.total) * 100
    : 0;

  const pageUsage: Distribution | null = useMemo(() => {
    // Absent on the screens that have no counters in scope (the general tab),
    // which is what keeps this pie off them.
    if (stats.black_pages === undefined) return null;
    const items = [
      { label: t("Black pages"), value: Number(stats.black_pages) || 0 },
      ...(hasColorEquipment
        ? [{ label: t("Color pages"), value: Number(stats.color_pages) || 0 }]
        : []),
      { label: t("Scanned pages"), value: Number(stats.scanned_pages) || 0 },
    ]
      .filter((item) => item.value > 0)
      .map((item) => ({ ...item, is_other: false }));

    if (items.length === 0) return null;
    return {
      items,
      total: items.reduce((sum, item) => sum + item.value, 0),
      other_count: 0,
    };
  }, [stats, hasColorEquipment, t]);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "repeat(2, minmax(0, 1fr))",
        },
        gap: 2,
      }}
    >
      <DistributionPie
        title={t("Used parts")}
        subtitle={
          partsByValue ? t("Share of parts spend") : t("Share of parts quantity")
        }
        distribution={stats.used_parts_distribution?.[partsMetric]}
        valueFormatter={partsByValue ? money : count}
        loading={loading}
        emptyMessage={t("No parts used in the selected period.")}
        action={
          <ToggleButtonGroup
            size="small"
            exclusive
            value={partsMetric}
            // Never deselects: a null value would leave the pie with no metric
            // to show, so ignoring the second click on the active button keeps
            // one always chosen.
            onChange={(_, next: ValueQuantityMetric | null) =>
              next && setPartsMetric(next)
            }
            aria-label={t("Used parts metric")}
          >
            <ToggleButton value="by_value">{t("Cost")}</ToggleButton>
            <ToggleButton value="by_quantity">{t("Quantity")}</ToggleButton>
          </ToggleButtonGroup>
        }
      />
      <DistributionPie
        title={t("Job Issues")}
        subtitle={t("Share of reported issues")}
        distribution={stats.job_issues_distribution}
        valueFormatter={count}
        loading={loading}
        emptyMessage={t("No issues reported in the selected period.")}
      />
      <DistributionPie
        title={t("Job Services")}
        subtitle={
          servicesByValue
            ? t("Share of performed services value")
            : t("Share of performed services")
        }
        distribution={stats.job_services_distribution?.[servicesMetric]}
        valueFormatter={servicesByValue ? money : count}
        loading={loading}
        emptyMessage={
          servicesByValue
            ? t("No priced services performed in the selected period.")
            : t("No services performed in the selected period.")
        }
        action={
          <ToggleButtonGroup
            size="small"
            exclusive
            value={servicesMetric}
            // Never deselects - see the used parts toggle above.
            onChange={(_, next: ValueQuantityMetric | null) =>
              next && setServicesMetric(next)
            }
            aria-label={t("Job services metric")}
          >
            <ToggleButton value="by_quantity">{t("Quantity")}</ToggleButton>
            <ToggleButton value="by_value">{t("Price")}</ToggleButton>
          </ToggleButtonGroup>
        }
      />

      {complaintsDistribution && (
        <DistributionPie
          title={t("Complaints")}
          subtitle={`${t("Complaint rate")}: ${formatQuantity(
            Math.round(complaintRate * 10) / 10
          )}%`}
          distribution={complaintsDistribution}
          valueFormatter={count}
          loading={loading}
          sliceColors={[palette.status.good, palette.status.critical]}
          emptyMessage={t("No jobs in the selected period.")}
        />
      )}

      {pageUsage && (
        <DistributionPie
          title={t("Page usage")}
          subtitle={t("Pages put through in the selected period")}
          distribution={pageUsage}
          valueFormatter={count}
          loading={loading}
          emptyMessage={t("No counter readings in the selected period.")}
        />
      )}
    </Box>
  );
}
